/* eslint-disable max-lines */
import * as React from 'react';
import { useRef, useCallback, useState, useImperativeHandle, forwardRef } from 'react';
import { Map as MapGL, Source, Layer, Popup, Marker } from 'react-map-gl/maplibre';
import type { MapRef, MapMouseEvent, ViewStateChangeEvent, MapEvent } from 'react-map-gl/maplibre';
import type { GeoJSONSource, Map as MapLibreMap } from 'maplibre-gl';
import { AttributionControl } from 'maplibre-gl';

export type MapInstance = MapLibreMap;
import 'maplibre-gl/dist/maplibre-gl.css';
import { cn } from '../lib/utils.js';
import { registerPoiIcons } from './map-view-poi-icons.js';
import { buildPoiLayerDef, poiFeaturesToGeoJson } from './map-view-poi-layer.js';
import type { PoiFeature } from './map-view-poi-layer.js';
export type { PoiFeature } from './map-view-poi-layer.js';
import {
  applyPixelOffset,
  computeSpiderfyLegOffsets,
  shouldSpiderfyCluster,
} from './map-view-spiderfy.js';

const OSM_ATTRIBUTION = '\u00a9 OpenStreetMap contributors';

// ----------------------------------------------------------------
// Drawing tool type
// ----------------------------------------------------------------

export type DrawingTool =
  | 'pan'
  | 'lasso-add'
  | 'lasso-remove'
  | 'lasso-fresh'
  | 'rectangle-add'
  | 'rectangle-remove'
  | 'rectangle-fresh'
  | 'circle-add'
  | 'circle-remove'
  | 'circle-fresh'
  | 'polygon-add'
  | 'polygon-remove'
  | 'polygon-fresh'
  | 'point-add'
  | 'point-remove';

// ----------------------------------------------------------------
// Imperative handle
// ----------------------------------------------------------------

export interface MapViewHandle {
  clearAllShapes: () => void;
}

// ----------------------------------------------------------------
// Types
// ----------------------------------------------------------------

export interface MapPin {
  id: string | number;
  latitude: number;
  longitude: number;
  /** Used for pin color differentiation */
  category: string;
  /** Arbitrary properties passed through to popup */
  properties?: Record<string, unknown>;
}

export interface MapViewProps {
  /** Array of pins to render on the map */
  pins: MapPin[];
  /** Optional secondary pin set rendered dimmed and clustered separately. */
  dimmedPins?: MapPin[];
  /** Array of POI features to render as a symbol layer (below media-unit pins). */
  poiData?: PoiFeature[];
  /** Whether to show the POI layer. Defaults to true. */
  showPoiLayer?: boolean;
  /** Set of visible POI category keys ("osmKey-osmValue"). Empty = all visible. */
  visiblePoiCategories?: Set<string>;
  /** Initial center [latitude, longitude]. Defaults to center of Poland. */
  initialCenter?: [number, number];
  /** Initial zoom level. Defaults to 6 (country-level for Poland). */
  initialZoom?: number;
  /**
   * Initial camera bounds as [west, south, east, north] — matches the format
   * `onBoundsChange` emits (split on comma). When provided, takes priority
   * over `initialCenter`/`initialZoom` for the map's first render (uncontrolled
   * `initialViewState`; has no effect on subsequent re-renders).
   */
  initialBounds?: [number, number, number, number];
  /** Map tile style URL. Defaults to CartoDB Positron (free, no key). */
  mapStyle?: string;
  /** Color mapping: category string -> hex color. Unknown categories fall back to gray. */
  categoryColors?: Record<string, string>;
  /** Render function for popup content when a pin is clicked */
  renderPopup?: (pin: MapPin, onClose: () => void) => React.ReactNode;
  /** Force a specific pin popup open by id. */
  selectedPinId?: string | number | null;
  /** Called when unclustered (individual) visible pins change (after pan/zoom). Clustered pins are not reported. */
  onUnclusteredPinsChange?: (visiblePinIds: (string | number)[]) => void;
  /** Extra CSS class on the container div */
  className?: string;
  /** Max zoom for clustering. Defaults to 14. */
  clusterMaxZoom?: number;
  /** Cluster radius in px. Defaults to 60. */
  clusterRadius?: number;
  /** Called when the map has loaded. Use this to obtain the MapLibre instance (e.g. for E2E tests). */
  onMapReady?: (map: MapInstance) => void;
  /** Called when a POI marker is clicked */
  onPoiClick?: (poiId: string) => void;
  /**
   * Render function for the POI popup. Receives the clicked POI id and a close callback.
   * The popup is anchored at the POI's lat/lng on the map (like a MapLibre Popup/InfoWindow).
   * If null/undefined is returned, no popup is shown.
   */
  renderPoiPopup?: (poiId: string, onClose: () => void) => React.ReactNode;
  /**
   * The currently selected POI id. When provided, the popup is shown at the POI's coordinates.
   * Set to null to close the popup.
   */
  selectedPoiId?: string | null;
  /**
   * Called when the user clicks an empty area on the map (not a pin or cluster).
   * Receives the clicked latitude and longitude.
   */
  onMapClick?: (lat: number, lng: number) => void;
  /**
   * When provided, renders a single marker at the given position.
   * Pass null to hide the marker.
   */
  markerPosition?: { lat: number; lng: number } | null;
  /**
   * Called when the visible map bounds change (on move end and initial load).
   * Receives the bbox string in "minLon,minLat,maxLon,maxLat" format.
   */
  onBoundsChange?: (bbox: string) => void;

  // ----------------------------------------------------------------
  // Drawing props (terra-draw integration)
  // ----------------------------------------------------------------

  /** When true, wires up terra-draw for shape drawing. Default: false. */
  enableDrawing?: boolean;
  /** The currently active drawing tool. Only used when enableDrawing is true. */
  currentTool?: DrawingTool;
  /** Called when a shape draw is completed. */
  onDrawComplete?: (geometry: GeoJSON.Polygon, toolUsed: DrawingTool) => void;
  /** Called continuously as a shape is being drawn (for live count badge). */
  onDrawProgress?: (geometry: GeoJSON.Polygon | null, toolUsed: DrawingTool) => void;
  /** Set of pin ids to render with a selected highlight overlay. */
  selectedPinIds?: ReadonlySet<string | number>;
  /**
   * Called when an individual (unclustered) pin is clicked, before the popup is shown.
   * Can be used to toggle pin selection state in the parent.
   */
  onPinClick?: (pinId: string | number) => void;
}

// ----------------------------------------------------------------
// Default style + colors
// ----------------------------------------------------------------

const DEFAULT_MAP_STYLE = 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json';
const DEFAULT_CENTER: [number, number] = [51.92, 19.15]; // Poland center
const DEFAULT_ZOOM = 6;
const FALLBACK_PIN_COLOR = '#6b7280'; // gray-500
const EMPTY_PINS: MapPin[] = [];
const CLUSTER_COLOR_STOPS = [
  'step',
  ['get', 'point_count'],
  '#6366f1',
  50,
  '#f59e0b',
  200,
  '#ef4444',
];
const CLUSTER_RADIUS_STOPS = ['step', ['get', 'point_count'], 18, 50, 24, 200, 32];

// ----------------------------------------------------------------
// Layer definitions (built dynamically from category colors)
// ----------------------------------------------------------------

/**
 * Cluster circle/count layer `filter` — excludes the currently spiderfied
 * cluster (by id) so its circle/count label disappear while its leaves are
 * spread out, instead of rendering both at once.
 */
function buildClusterFilter(excludeClusterId: number | null): unknown[] {
  const base = ['has', 'point_count'];
  return excludeClusterId == null
    ? base
    : ['all', base, ['!=', ['get', 'cluster_id'], excludeClusterId]];
}

/** Cluster circles use a fixed indigo/amber/red palette regardless of category colors. */
function buildClusterLayer(
  source = 'pins',
  opacity = 0.9,
  strokeOpacity = 1,
  excludeClusterId: number | null = null,
) {
  return {
    id: source === 'pins' ? 'clusters' : 'dimmed-clusters',
    type: 'circle',
    source,
    filter: buildClusterFilter(excludeClusterId),
    paint: {
      'circle-color': CLUSTER_COLOR_STOPS,
      'circle-radius': CLUSTER_RADIUS_STOPS,
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff',
      'circle-opacity': opacity,
      'circle-stroke-opacity': strokeOpacity,
    },
  };
}

function buildClusterCountLayer(
  source = 'pins',
  textColor = '#ffffff',
  excludeClusterId: number | null = null,
) {
  return {
    id: source === 'pins' ? 'cluster-count' : 'dimmed-cluster-count',
    type: 'symbol',
    source,
    filter: buildClusterFilter(excludeClusterId),
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
      'text-size': 13,
    },
    paint: {
      'text-color': textColor,
    },
  };
}

/**
 * Build the `circle-color` paint value for the unclustered pin layer.
 *
 * MapLibre's `match` expression requires the form
 * `['match', input, label1, output1, ..., fallback]` — at least one
 * label/output pair, i.e. 4+ arguments. When `colors` is empty (the default,
 * used by the internal offer units map which does not color pins by category),
 * a `match` with only `['match', input, fallback]` is malformed and MapLibre
 * rejects `addLayer` with "Expected at least 4 arguments, but found only 2."
 * In that case return the constant fallback color instead of a degenerate match.
 */
export function buildUnclusteredColorExpression(colors: Record<string, string>): unknown {
  const entries = Object.entries(colors);
  if (entries.length === 0) {
    return FALLBACK_PIN_COLOR;
  }
  // ['match', ['get', 'category'], cat1, color1, ..., fallback]
  const matchExpr: unknown[] = ['match', ['get', 'category']];
  for (const [cat, color] of entries) {
    matchExpr.push(cat, color);
  }
  matchExpr.push(FALLBACK_PIN_COLOR);
  return matchExpr;
}

function buildUnclusteredLayer(
  colors: Record<string, string>,
  source = 'pins',
  opacity = 1,
  strokeOpacity = 1,
) {
  const colorExpr = buildUnclusteredColorExpression(colors);

  // For the primary 'pins' source, use feature-state to express selection highlight.
  // Slightly larger radius and thicker stroke when feature-state.selected is true.
  // This keeps selected pins inside the clustered source instead of a separate
  // non-clustered source (which caused all selected pins to bypass clustering).
  const circleRadius =
    source === 'pins'
      ? (['case', ['boolean', ['feature-state', 'selected'], false], 9, 7] as unknown)
      : 7;
  const circleStrokeWidth =
    source === 'pins'
      ? (['case', ['boolean', ['feature-state', 'selected'], false], 3, 2] as unknown)
      : 2;

  return {
    id: source === 'pins' ? 'unclustered-point' : 'dimmed-unclustered-point',
    type: 'circle' as const,
    source,
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': colorExpr,
      'circle-radius': circleRadius,
      'circle-stroke-width': circleStrokeWidth,
      'circle-stroke-color': '#ffffff',
      'circle-opacity': opacity,
      'circle-stroke-opacity': strokeOpacity,
    },
  };
}

// ----------------------------------------------------------------
// Drawing helpers
// ----------------------------------------------------------------

function toolToTerraDrawMode(tool: DrawingTool): string {
  if (tool.startsWith('lasso')) return 'freehand';
  if (tool.startsWith('rectangle')) return 'rectangle';
  if (tool.startsWith('circle')) return 'circle';
  if (tool.startsWith('polygon')) return 'polygon';
  // 'point-add', 'point-remove', and 'pan' use the idle select mode — no shape drawing
  return 'select';
}

// ----------------------------------------------------------------
// Spiderfy state — a cluster that could not be zoomed apart further, spread
// into a radial layout of its individual leaf pins. See map-view-spiderfy.ts.
// ----------------------------------------------------------------

interface SpiderfyState {
  clusterId: number;
  /** Which clustered GeoJSON source the cluster belongs to. */
  sourceId: 'pins' | 'dimmed-pins';
  center: { lat: number; lng: number };
  pins: MapPin[];
}

interface SpiderfyLeg {
  pin: MapPin;
  position: { lat: number; lng: number };
}

/**
 * Resolves each spiderfied leaf's radial screen position from the cluster
 * center, using the map instance's own `project`/`unproject` so the result
 * accounts for the map's current bearing, pitch, and projection — a flat
 * meters-per-pixel approximation drifts under rotation/tilt. Computed once
 * per spiderfy state (memoized on `spiderfy` only), not re-computed on pan —
 * the resolved lat/lng Markers then pan/zoom with the map on their own.
 */
function resolveSpiderfyLegs(spiderfy: SpiderfyState, map: MapLibreMap): SpiderfyLeg[] {
  const offsets = computeSpiderfyLegOffsets(spiderfy.pins.length);
  const centerPoint = map.project([spiderfy.center.lng, spiderfy.center.lat]);
  const legs: SpiderfyLeg[] = [];
  for (let i = 0; i < spiderfy.pins.length; i++) {
    const pin = spiderfy.pins[i];
    const offset = offsets[i];
    if (!pin || !offset) continue;
    const point = applyPixelOffset(centerPoint, offset);
    const lngLat = map.unproject([point.x, point.y]);
    legs.push({ pin, position: { lat: lngLat.lat, lng: lngLat.lng } });
  }
  return legs;
}

const SPIDERFY_LINES_LAYER = {
  id: 'spiderfy-lines-layer',
  type: 'line',
  source: 'spiderfy-lines',
  paint: {
    'line-color': '#9ca3af',
    'line-width': 1.5,
  },
};

/** Builds one connector-line GeoJSON feature per leg, from the cluster center to the leaf. */
function buildSpiderfyLinesGeoJson(
  center: { lat: number; lng: number },
  legs: SpiderfyLeg[],
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: legs.map((leg) => ({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: [
          [center.lng, center.lat],
          [leg.position.lng, leg.position.lat],
        ],
      },
    })),
  };
}

// ----------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------

// maplibre rejects with a plain Error, so the message is the only discriminator.
function isStaleClusterError(error: unknown): boolean {
  return error instanceof Error && error.message.startsWith('No cluster with the specified id');
}

async function lookupClusterOrNull<T>(lookup: () => Promise<T>): Promise<T | null> {
  try {
    return await lookup();
  } catch (error) {
    if (isStaleClusterError(error)) return null;
    throw error;
  }
}

function pinsToGeoJson(pins: MapPin[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: pins.map((pin) => ({
      type: 'Feature',
      id: pin.id,
      geometry: {
        type: 'Point',
        coordinates: [pin.longitude, pin.latitude],
      },
      properties: {
        pinId: String(pin.id),
        category: pin.category,
        ...pin.properties,
      },
    })),
  };
}

// ----------------------------------------------------------------
// Component
// ----------------------------------------------------------------

// eslint-disable-next-line complexity, max-lines-per-function
export const MapView = forwardRef<MapViewHandle, MapViewProps>(function MapView(
  {
    pins,
    dimmedPins = EMPTY_PINS,
    poiData,
    showPoiLayer = true,
    visiblePoiCategories,
    initialCenter = DEFAULT_CENTER,
    initialZoom = DEFAULT_ZOOM,
    initialBounds,
    mapStyle = DEFAULT_MAP_STYLE,
    categoryColors = {},
    renderPopup,
    selectedPinId,
    onUnclusteredPinsChange,
    className,
    clusterMaxZoom = 14,
    clusterRadius = 60,
    onMapReady,
    onPoiClick,
    renderPoiPopup,
    selectedPoiId,
    onMapClick,
    markerPosition,
    onBoundsChange,
    enableDrawing = false,
    currentTool,
    onDrawComplete,
    onDrawProgress,
    selectedPinIds,
    onPinClick,
  },
  ref,
) {
  const mapRef = useRef<MapRef>(null);
  const [selectedPin, setSelectedPin] = useState<MapPin | null>(null);
  const [selectedPoiCoords, setSelectedPoiCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  // Spread-out leaves of a cluster that could not be zoomed apart further (see
  // map-view-spiderfy.ts). Cleared on zoom or on any other map/pin click.
  const [spiderfy, setSpiderfy] = useState<SpiderfyState | null>(null);

  // terra-draw refs — we store the draw instance in a ref so cleanup always
  // has access to the latest instance without re-running effects.
  const drawRef = useRef<{
    start: () => void;
    stop: () => void;
    setMode: (mode: string) => void;
    on: (event: string, cb: (...args: unknown[]) => void) => void;
    getSnapshotFeature: (id: unknown) => { geometry: GeoJSON.Geometry } | undefined;
    getSnapshot: () => { geometry: GeoJSON.Geometry }[];
    removeFeatures: (ids: unknown[]) => void;
    clear: () => void;
  } | null>(null);
  const adapterRef = useRef<{ unregister: () => void } | null>(null);

  // Keep a ref of currentTool so event handlers always read the latest value
  // without being re-created every render.
  const currentToolRef = useRef<DrawingTool>(currentTool ?? 'pan');
  currentToolRef.current = currentTool ?? 'pan';

  const onDrawCompleteRef = useRef(onDrawComplete);
  onDrawCompleteRef.current = onDrawComplete;
  const onDrawProgressRef = useRef(onDrawProgress);
  onDrawProgressRef.current = onDrawProgress;

  // Expose clearAllShapes via imperative handle
  useImperativeHandle(ref, () => ({
    clearAllShapes: () => {
      drawRef.current?.clear();
    },
  }));

  const emitBounds = useCallback(() => {
    if (!onBoundsChange || !mapRef.current) return;
    const b = mapRef.current.getMap().getBounds();
    onBoundsChange(`${b.getWest()},${b.getSouth()},${b.getEast()},${b.getNorth()}`);
  }, [onBoundsChange]);

  const onMapLoad = useCallback(
    (event: MapEvent) => {
      const map = event.target;

      // Add OSM attribution control (non-removable)
      map.addControl(
        new AttributionControl({
          customAttribution: OSM_ATTRIBUTION,
          compact: false,
        }),
      );

      // Register canvas-drawn POI icons for all categories
      registerPoiIcons(map);

      // A genuine zoom (scroll/pinch/double-click) collapses any active
      // spiderfy spread — the leaves re-cluster or separate on their own at
      // the new zoom level. Deliberately NOT 'movestart': a leaf-pin click's
      // own camera pan (to keep its popup visible) must not immediately
      // collapse the spread the user just interacted with.
      map.on('zoomstart', () => setSpiderfy(null));

      if (onMapReady) {
        onMapReady(map);
      }

      emitBounds();

      // Dynamically load terra-draw only when enableDrawing is true
      if (!enableDrawing) return;

      // terra-draw is dynamically imported to keep it out of the main bundle.
      // The library's dynamic exports are untyped (`any`) — disable unsafe rules
      // for this block only. The ref type cast at the bottom ensures the stored
      // instance still conforms to the typed interface in drawRef.
      /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */
      void (async () => {
        const {
          TerraDraw,
          TerraDrawFreehandMode,
          TerraDrawRectangleMode,
          TerraDrawCircleMode,
          TerraDrawPolygonMode,
          TerraDrawSelectMode,
        } = await import('terra-draw');
        const { TerraDrawMapLibreGLAdapter } = await import('terra-draw-maplibre-gl-adapter');

        const adapter = new TerraDrawMapLibreGLAdapter({ map });
        adapterRef.current = adapter as { unregister: () => void };

        const draw = new TerraDraw({
          adapter,
          modes: [
            // 'click-move-or-drag' lets each mode complete via click-move-click
            // OR a real mousedown-drag-mouseup gesture; the maplibre adapter
            // toggles map dragPan off/on internally during the drag, so panning
            // is suppressed only while a drag-to-draw gesture is in progress.
            new TerraDrawFreehandMode({ minDistance: 5, drawInteraction: 'click-move-or-drag' }),
            new TerraDrawRectangleMode({ drawInteraction: 'click-move-or-drag' }),
            new TerraDrawCircleMode({ drawInteraction: 'click-move-or-drag' }),
            new TerraDrawPolygonMode(),
            new TerraDrawSelectMode(),
          ],
        });

        draw.start();
        draw.setMode(toolToTerraDrawMode(currentToolRef.current));

        // finish event — shape is complete
        // terra-draw FeatureId = string | number
        draw.on('finish', (id: string | number) => {
          const feature = draw.getSnapshotFeature(id);
          if (feature?.geometry?.type === 'Polygon') {
            onDrawCompleteRef.current?.(
              feature.geometry as GeoJSON.Polygon,
              currentToolRef.current,
            );
          }
          // Signal progress cleared
          onDrawProgressRef.current?.(null, currentToolRef.current);
          // All shapes are ephemeral — remove immediately after processing.
          // Visual feedback of selection comes from highlighted pins, not the lasso.
          try {
            draw.removeFeatures([id]);
          } catch {
            /* ignore */
          }
        });

        // change event — shape is being drawn
        // eslint-disable-next-line complexity
        draw.on('change', (ids: (string | number)[], type: string) => {
          if (type === 'update' || type === 'create') {
            for (const id of ids) {
              const feature = draw.getSnapshotFeature(id);
              if (feature?.geometry?.type === 'Polygon') {
                onDrawProgressRef.current?.(
                  feature.geometry as GeoJSON.Polygon,
                  currentToolRef.current,
                );
              }
            }
          }
        });

        // Store in ref — stable, no re-render needed
        drawRef.current = draw as typeof drawRef.current;
      })();
      /* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */
    },
    [onMapReady, emitBounds, enableDrawing],
  );

  // Sync tool mode when currentTool prop changes
  React.useEffect(() => {
    if (!drawRef.current || !currentTool) return;
    drawRef.current.setMode(toolToTerraDrawMode(currentTool));
  }, [currentTool]);

  // Cleanup terra-draw on unmount.
  // unregister() attempts to remove MapLibre layers/sources; if the map is
  // already being destroyed those removals throw. Swallow — the map is gone.
  React.useEffect(() => {
    return () => {
      try {
        drawRef.current?.stop();
        adapterRef.current?.unregister();
      } catch {
        // Map already removed — layers no longer exist
      }
    };
  }, []);

  const geojson = React.useMemo(() => pinsToGeoJson(pins), [pins]);
  const dimmedGeojson = React.useMemo(() => pinsToGeoJson(dimmedPins), [dimmedPins]);
  const poiGeoJson = React.useMemo(() => poiFeaturesToGeoJson(poiData ?? []), [poiData]);
  const pinLookup = React.useMemo(() => {
    const map = new Map<string, MapPin>();
    for (const pin of [...pins, ...dimmedPins]) {
      map.set(String(pin.id), pin);
    }
    return map;
  }, [pins, dimmedPins]);

  // Track the previously-selected set so we can clear deselected feature states.
  // MapLibre has no bulk-clear API for feature state, so we diff old vs new on each change.
  const prevSelectedRef = React.useRef<ReadonlySet<string | number>>(new Set());

  // Sync feature-state on the clustered 'pins' source whenever selectedPinIds changes.
  // Using setFeatureState keeps selection inside the existing clustered source so that
  // selected pins still cluster at low zoom levels (D-5 spec requirement).
  React.useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !map.isStyleLoaded()) return;

    const prev = prevSelectedRef.current;
    const next = selectedPinIds ?? new Set<string | number>();

    // Clear deselected
    for (const id of prev) {
      if (!next.has(id)) {
        map.setFeatureState({ source: 'pins', id }, { selected: false });
      }
    }
    // Mark newly selected
    for (const id of next) {
      if (!prev.has(id)) {
        map.setFeatureState({ source: 'pins', id }, { selected: true });
      }
    }

    prevSelectedRef.current = next;
  }, [selectedPinIds]);

  // Clear selectedPin when selectedPinId is explicitly cleared.
  // This effect only watches selectedPinId so that pinLookup changes (e.g. when
  // dimmedPins updates after a toggleOne) do NOT reset a popup that was opened via
  // a direct canvas click (setSelectedPin called in the onClick handler).
  React.useEffect(() => {
    if (selectedPinId == null) setSelectedPin(null);
  }, [selectedPinId]);

  // Sync selectedPin object when selectedPinId is set, or when pinLookup updates
  // (e.g. new pins loaded) while a pin is already selected via selectedPinId.
  React.useEffect(() => {
    if (selectedPinId == null) return;
    const pin = pinLookup.get(String(selectedPinId));
    if (pin) setSelectedPin(pin);
  }, [selectedPinId, pinLookup]);

  const poiLookup = React.useMemo(() => {
    const map = new Map<string, PoiFeature>();
    for (const poi of poiData ?? []) {
      map.set(poi.id, poi);
    }
    return map;
  }, [poiData]);

  // Sync selectedPoiCoords when selectedPoiId changes from outside
  React.useEffect(() => {
    if (!selectedPoiId) {
      setSelectedPoiCoords(null);
      return;
    }
    const poi = poiLookup.get(selectedPoiId);
    if (poi) {
      setSelectedPoiCoords({ lat: poi.lat, lng: poi.lng });
    }
  }, [selectedPoiId, poiLookup]);

  // Excludes the currently spiderfied cluster's circle/count label from its source
  // — the spread leaves replace it visually instead of both rendering at once.
  const spiderfiedClusterId = React.useMemo(
    () => (spiderfy ? spiderfy.clusterId : null),
    [spiderfy],
  );
  const pinsSpiderfiedClusterId = spiderfy?.sourceId === 'pins' ? spiderfiedClusterId : null;
  const dimmedSpiderfiedClusterId =
    spiderfy?.sourceId === 'dimmed-pins' ? spiderfiedClusterId : null;
  const clusterLayer = React.useMemo(
    () => buildClusterLayer('pins', 0.9, 1, pinsSpiderfiedClusterId),
    [pinsSpiderfiedClusterId],
  );
  const dimmedClusterLayer = React.useMemo(
    () => buildClusterLayer('dimmed-pins', 0.3, 0.3, dimmedSpiderfiedClusterId),
    [dimmedSpiderfiedClusterId],
  );
  const clusterCountLayer = React.useMemo(
    () => buildClusterCountLayer('pins', '#ffffff', pinsSpiderfiedClusterId),
    [pinsSpiderfiedClusterId],
  );
  const dimmedClusterCountLayer = React.useMemo(
    () => buildClusterCountLayer('dimmed-pins', 'rgba(255,255,255,0.6)', dimmedSpiderfiedClusterId),
    [dimmedSpiderfiedClusterId],
  );
  const unclusteredLayer = React.useMemo(
    () => buildUnclusteredLayer(categoryColors),
    [categoryColors],
  );
  const dimmedUnclusteredLayer = React.useMemo(
    () => buildUnclusteredLayer(categoryColors, 'dimmed-pins', 0.3, 0.3),
    [categoryColors],
  );

  // Click handler: expand cluster or select pin or POI
  const onClick = useCallback(
    // eslint-disable-next-line complexity
    async (event: MapMouseEvent) => {
      const feature = event.features?.[0];
      if (!feature) {
        setSelectedPin(null);
        setSpiderfy(null);
        if (onMapClick) {
          onMapClick(event.lngLat.lat, event.lngLat.lng);
        }
        return;
      }

      const props = feature.properties as Record<string, unknown> | null;

      // POI layer click
      if (feature.layer?.id === 'poi-layer') {
        setSpiderfy(null);
        const poiId = props?.poi_id;
        if (poiId) {
          const poiIdStr = String(poiId);
          const poi = poiLookup.get(poiIdStr);
          if (poi) {
            setSelectedPoiCoords({ lat: poi.lat, lng: poi.lng });
          }
          if (onPoiClick) {
            onPoiClick(poiIdStr);
          }
        }
        return;
      }

      // Cluster click -> zoom in, or spiderfy (spread leaves radially) when
      // zooming further would not visually separate them (near-identical
      // coordinates, or already at the source's clusterMaxZoom).
      if (props?.cluster_id) {
        const sourceId: 'pins' | 'dimmed-pins' =
          feature.source === 'dimmed-pins' ? 'dimmed-pins' : 'pins';
        const source = mapRef.current?.getSource(sourceId) as GeoJSONSource | undefined;
        if (!source) return;
        const clusterId = props.cluster_id as number;
        const center = (feature.geometry as GeoJSON.Point).coordinates as [number, number];
        const currentZoom = mapRef.current?.getMap().getZoom() ?? initialZoom;
        const expansionZoom = await lookupClusterOrNull(() =>
          source.getClusterExpansionZoom(clusterId),
        );
        if (expansionZoom === null) {
          setSpiderfy(null);
          return;
        }

        if (shouldSpiderfyCluster(expansionZoom, currentZoom, clusterMaxZoom)) {
          const pointCount = (props.point_count as number | undefined) ?? 0;
          const leaves = await lookupClusterOrNull(() =>
            source.getClusterLeaves(clusterId, pointCount, 0),
          );
          if (!leaves) {
            setSpiderfy(null);
            return;
          }
          const leafPins = leaves
            .map((leaf) => {
              const leafPinId = (leaf.properties as Record<string, unknown> | null)?.pinId;
              return leafPinId != null ? (pinLookup.get(String(leafPinId)) ?? null) : null;
            })
            .filter((p): p is MapPin => p !== null);
          setSpiderfy({
            clusterId,
            sourceId,
            center: { lng: center[0], lat: center[1] },
            pins: leafPins,
          });
          return;
        }

        setSpiderfy(null);
        mapRef.current?.easeTo({ center, zoom: expansionZoom, duration: 500 });
        return;
      }

      setSpiderfy(null);

      // Individual pin click - pan so pin is visible with popup above
      const pinId = props?.pinId;
      if (pinId) {
        const pin = pinLookup.get(String(pinId));
        if (pin && mapRef.current) {
          if (onPinClick) {
            onPinClick(pin.id);
          }
          // In click-select mode (point-add / point-remove) pin clicks add/remove
          // from the selection set. Showing a popup or panning the map is wrong:
          // it gives visual feedback that implies "details opened" rather than
          // "unit selected". Skip both setSelectedPin and easeTo in that mode.
          const tool = currentToolRef.current;
          const inClickSelectMode = tool === 'point-add' || tool === 'point-remove';
          if (!inClickSelectMode) {
            setSelectedPin(pin);
          }
          // Only pan the camera when a popup will actually be rendered — the pan
          // exists solely to keep the popup (anchored above the pin) visible.
          // Consumers that only use selectedPinIds/selectedPinId for a purely
          // visual highlight (no popup) must not have their camera moved.
          if (!inClickSelectMode && renderPopup) {
            // Fixed offset to push the map center up, placing the pin in the lower
            // half so the popup (anchored above the pin via Popup offset={12}) fits.
            const PAN_OFFSET_Y = 150;
            mapRef.current.easeTo({
              center: [pin.longitude, pin.latitude],
              offset: [0, PAN_OFFSET_Y],
              duration: 300,
            });
          }
        }
      }
    },
    [
      pinLookup,
      poiLookup,
      onPoiClick,
      onMapClick,
      onPinClick,
      renderPopup,
      clusterMaxZoom,
      initialZoom,
    ],
  );

  // Leaf-pin click inside a spiderfied spread — opens the same pin popup as a
  // normal individual pin click, but does not collapse the spread (the leaf
  // Marker's DOM click never reaches the map canvas's onClick above).
  const handleSpiderfyLeafClick = useCallback(
    (pin: MapPin) => {
      if (onPinClick) onPinClick(pin.id);
      setSelectedPin(pin);
    },
    [onPinClick],
  );

  // Report visible pins on move
  const handleMoveEnd = useCallback(
    (_e: ViewStateChangeEvent) => {
      if (onUnclusteredPinsChange && mapRef.current) {
        const map = mapRef.current.getMap();
        const features = map.queryRenderedFeatures(undefined, {
          layers: ['unclustered-point'],
        });
        const ids = features
          .map((f) => (f.properties as Record<string, unknown> | null)?.pinId)
          .filter((id): id is string | number => Boolean(id));
        onUnclusteredPinsChange(ids);
      }

      emitBounds();
    },
    [onUnclusteredPinsChange, emitBounds],
  );

  const interactiveLayerIds = React.useMemo(() => {
    const ids: string[] = ['clusters', 'unclustered-point'];
    if (dimmedPins.length > 0) ids.push('dimmed-clusters', 'dimmed-unclustered-point');
    if (showPoiLayer && poiData && poiData.length > 0) ids.push('poi-layer');
    return ids;
  }, [showPoiLayer, poiData, dimmedPins.length]);

  const spiderfyLegs = React.useMemo(() => {
    const map = mapRef.current?.getMap();
    if (!spiderfy || !map) return [];
    return resolveSpiderfyLegs(spiderfy, map);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mapRef is a stable ref; only `spiderfy` should retrigger this memo.
  }, [spiderfy]);
  const spiderfyLinesGeoJson = React.useMemo(
    () => (spiderfy ? buildSpiderfyLinesGeoJson(spiderfy.center, spiderfyLegs) : null),
    [spiderfy, spiderfyLegs],
  );

  return (
    <div className={cn(className)} style={{ width: '100%', height: '100%' }}>
      <MapGL
        ref={mapRef}
        initialViewState={
          initialBounds
            ? { bounds: initialBounds }
            : {
                latitude: initialCenter[0],
                longitude: initialCenter[1],
                zoom: initialZoom,
              }
        }
        mapStyle={mapStyle}
        interactiveLayerIds={interactiveLayerIds}
        onClick={onClick}
        onLoad={onMapLoad}
        onMoveEnd={handleMoveEnd}
        style={{ width: '100%', height: '100%' }}
        cursor={'pointer'} // eslint-disable-line i18next/no-literal-string
        // Disable default attribution so we control it via AttributionControl
        attributionControl={false}
      >
        {/* POI layer rendered BEFORE pin layers so it sits below media-unit pins */}
        {showPoiLayer && poiData && poiData.length > 0 ? (
          <Source id="poi-layer-source" type="geojson" data={poiGeoJson}>
            {/*
             * maplibre-gl's `LayerSpecification` is a discriminated union of all layer types,
             * while react-map-gl/maplibre's `LayerProps` uses its own narrower union that does
             * not structurally overlap cleanly enough for JSX spread. TypeScript cannot reconcile
             * the two through spread without an explicit cast, so `as any` is required here.
             */}
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <Layer {...(buildPoiLayerDef(visiblePoiCategories) as any)} />
          </Source>
        ) : null}

        <Source
          id="pins"
          type="geojson"
          data={geojson}
          cluster={true}
          // clusterMaxZoom must stay below this source's tile maxzoom — no explicit
          // `maxzoom` prop is set here, so it silently inherits MapLibre's internal
          // default of 18. A clusterMaxZoom >= that tile maxzoom means the last
          // loaded tile is still fully clustered, so points never separate at any
          // camera zoom. See CU-86cb1nq3g and the `clusterMaxZoom={16}` override at
          // apps/web/src/routes/map/index.tsx (MapViewWithDrawing call site) that
          // depends on this constraint — changing this default or adding an
          // explicit `maxzoom` here can silently reintroduce that bug.
          clusterMaxZoom={clusterMaxZoom}
          clusterRadius={clusterRadius}
        >
          {/*
           * maplibre-gl's `LayerSpecification` is a discriminated union of all layer types,
           * while react-map-gl/maplibre's `LayerProps` uses its own narrower union that does
           * not structurally overlap cleanly enough for JSX spread. TypeScript cannot reconcile
           * the two through spread without an explicit cast, so `as any` is required here.
           */}
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <Layer {...(clusterLayer as any)} />
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <Layer {...(clusterCountLayer as any)} />
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <Layer {...(unclusteredLayer as any)} />
        </Source>

        {dimmedPins.length > 0 ? (
          <Source
            id="dimmed-pins"
            type="geojson"
            data={dimmedGeojson}
            cluster={true}
            // Same tile-maxzoom constraint as the 'pins' Source above — see the
            // comment there and CU-86cb1nq3g.
            clusterMaxZoom={clusterMaxZoom}
            clusterRadius={clusterRadius}
          >
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <Layer {...(dimmedClusterLayer as any)} />
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <Layer {...(dimmedClusterCountLayer as any)} />
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <Layer {...(dimmedUnclusteredLayer as any)} />
          </Source>
        ) : null}

        {selectedPin && renderPopup ? (
          <Popup
            longitude={selectedPin.longitude}
            latitude={selectedPin.latitude}
            anchor={'bottom'}
            closeButton={false}
            closeOnClick={false}
            onClose={() => setSelectedPin(null)}
            offset={12}
            maxWidth={'360px'} // eslint-disable-line i18next/no-literal-string
            className="map-pin-popup"
          >
            {renderPopup(selectedPin, () => setSelectedPin(null))}
          </Popup>
        ) : null}

        {selectedPoiId && selectedPoiCoords && renderPoiPopup ? (
          <Popup
            longitude={selectedPoiCoords.lng}
            latitude={selectedPoiCoords.lat}
            anchor={'bottom'}
            closeButton={false}
            closeOnClick={false}
            onClose={() => setSelectedPoiCoords(null)}
            offset={12}
            maxWidth={'320px'} // eslint-disable-line i18next/no-literal-string
            className="map-pin-popup"
          >
            {renderPoiPopup(selectedPoiId, () => setSelectedPoiCoords(null))}
          </Popup>
        ) : null}

        {markerPosition ? (
          <Marker latitude={markerPosition.lat} longitude={markerPosition.lng} />
        ) : null}

        {/* Spiderfy — leaves of a cluster that could not be zoomed apart further,
            spread into a radial layout with thin connector lines back to the
            cluster center. See map-view-spiderfy.ts for the offset math. */}
        {spiderfy && spiderfyLinesGeoJson ? (
          <Source id="spiderfy-lines" type="geojson" data={spiderfyLinesGeoJson}>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- see other Layer spreads above */}
            <Layer {...(SPIDERFY_LINES_LAYER as any)} />
          </Source>
        ) : null}
        {spiderfyLegs.map(({ pin, position }) => (
          <Marker
            key={`spiderfy-leaf-${pin.id}`}
            latitude={position.lat}
            longitude={position.lng}
            onClick={(e) => {
              e.originalEvent.stopPropagation();
              handleSpiderfyLeafClick(pin);
            }}
          >
            <span
              data-testid={`spiderfy-leaf-${pin.id}`}
              className="block h-4 w-4 rounded-full border-2 border-white shadow-md cursor-pointer"
              // DS-BYPASS: backgroundColor is per-pin dynamic (category color map), not a
              // fixed design-system token — cannot be expressed as a Tailwind class.
              style={{ backgroundColor: categoryColors[pin.category] ?? FALLBACK_PIN_COLOR }}
            />
          </Marker>
        ))}
      </MapGL>
    </div>
  );
});
